"use client";

import {
  LegalBulletList,
  LegalCtaBand,
  LegalDocumentShell,
  LegalLedger,
  LegalLedgerRow,
  LegalMeta,
  LegalParagraphs,
} from "@/components/marketing/legal-document/LegalDocumentShell";
import { AI_DISCLAIMER_ITEMS, TERMS_SECTIONS } from "../data";
import { SUPPORT_EMAIL } from "@/lib/contact";

const CURRENT_DATE = "May 26, 2026";
const COMPANY_LINE = "Zom Technologies LLC | Pennsylvania, United States";

const IMPORTANT_FINANCIAL_DISCLAIMER =
  "ZOM AI is a technology platform that provides research, analysis, and insights for financial professionals. ZOM is not a registered investment advisor, broker-dealer, or financial advisor. The Services are tools to assist with research and analysis and should not be used as the sole basis for financial decisions. All investment and financial decisions should be made in consultation with qualified financial professionals who understand your specific circumstances.";

const MSA_INTRO_PARAGRAPHS = [
  'This ZOM Master Subscription Agreement ("MSA") entered into by and between Zom Technologies LLC (or its applicable affiliate, "ZOM") and the entity or person executing an order form for the Services that expressly references this MSA ("Order Form") or signing up for and accessing the Services ("Customer") (each of Customer and ZOM, a "Party", and together, the "Parties").',
  `The "Effective Date" of this MSA is (a) the effective date of the first Order Form executed by the Parties, or (b) the date Customer first accesses the Services accepting this MSA through ZOM's sign-up process, as applicable.`,
] as const;

const NOT_FINANCIAL_ADVISOR_PARAGRAPHS = [
  "ZOM IS NOT A REGISTERED INVESTMENT ADVISOR, BROKER-DEALER, OR FINANCIAL ADVISOR.",
  "ZOM does not provide investment, financial, tax, or legal advice. The Services are technology tools designed to assist financial professionals with research, analysis, and data processing. ZOM does not recommend, endorse, or advise on any specific securities, investments, investment strategies, or financial products.",
] as const;

const AI_OUTPUTS_INTRO =
  'The Services utilize artificial intelligence, machine learning, and similar technologies to generate research, analysis, insights, and recommendations ("AI Outputs"). Customer acknowledges and agrees that:';

const PROFESSIONAL_RESPONSIBILITY_INTRO =
  "Customer acknowledges that it is a licensed financial professional subject to applicable regulatory requirements, fiduciary duties, and standards of care. Customer agrees that:";

const PROFESSIONAL_RESPONSIBILITY = [
  "All AI Outputs are provided for informational and research purposes only and are intended solely as a starting point for Customer's own independent analysis",
  "Customer bears sole and exclusive responsibility for verifying the accuracy, completeness, and appropriateness of any AI Output before relying on it or communicating it to clients",
  "Customer shall not present AI Outputs to clients as personalized investment advice, financial planning recommendations, or professional opinions without first applying Customer's own independent professional judgment and due diligence",
  "Customer remains solely responsible for all investment recommendations, financial advice, and fiduciary obligations owed to Customer's clients, regardless of whether such recommendations were informed by AI Outputs",
  "ZOM shall have no liability whatsoever for any actions taken or omitted by Customer or Customer's clients based on AI Outputs",
] as const;

const LIMITATION_OF_LIABILITY_PARAGRAPHS = [
  "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, UNDER NO CIRCUMSTANCES AND UNDER NO LEGAL THEORY WILL EITHER PARTY OR ITS AFFILIATES BE LIABLE TO THE OTHER PARTY FOR ANY LOST PROFITS, LOST SALES OR BUSINESS, LOST DATA, BUSINESS INTERRUPTION, OR FOR ANY TYPE OF INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, CONSEQUENTIAL OR PUNITIVE LOSS OR DAMAGES.",
  "WITHOUT LIMITING THE FOREGOING, ZOM SHALL NOT BE LIABLE FOR ANY INVESTMENT LOSSES, FINANCIAL DAMAGES, OR ADVERSE CONSEQUENCES RESULTING FROM CUSTOMER'S OR ITS CLIENTS' USE OF OR RELIANCE ON ANY INFORMATION, ANALYSIS, OR OUTPUTS PROVIDED BY THE SERVICES.",
] as const;

export default function TermsPage() {
  return (
    <LegalDocumentShell
      eyebrow="Legal"
      title="Terms of Service"
      description={`Last revised: ${CURRENT_DATE}`}
      meta={
        <>
          <LegalMeta>ZOM</LegalMeta>
          <LegalMeta>{COMPANY_LINE}</LegalMeta>
        </>
      }
      sections={[
        {
          id: "important-financial-disclaimer",
          eyebrow: "Important Financial Disclaimer",
          title: "Important Financial Disclaimer",
          description: IMPORTANT_FINANCIAL_DISCLAIMER,
        },
        {
          id: "master-subscription-agreement",
          eyebrow: "Master Subscription Agreement",
          title: 'ZOM Master Subscription Agreement ("MSA")',
          children: <LegalParagraphs paragraphs={MSA_INTRO_PARAGRAPHS} />,
        },
        ...TERMS_SECTIONS.map((section) => ({
          id: section.id,
          eyebrow: section.title,
          title: `${section.number} ${section.title}`,
          children: (
            <LegalLedger>
              {section.subsections.map((subsection, subIndex) => {
                const match = /^(\d+\.\d+)\s+(.+)$/.exec(subsection.title);
                const number =
                  match?.[1] ?? `${section.number}.${subIndex + 1}`;

                return (
                  <LegalLedgerRow
                    key={subsection.title}
                    number={number}
                    title={subsection.title}
                  >
                    <p>{subsection.content}</p>
                  </LegalLedgerRow>
                );
              })}
            </LegalLedger>
          ),
        })),
        {
          id: "financial-services-disclaimers",
          eyebrow: "Critical Terms",
          title: "Financial Services Disclaimers",
          children: (
            <LegalLedger>
              <LegalLedgerRow number="5.1" title="5.1 Not a Financial Advisor">
                <LegalParagraphs
                  paragraphs={NOT_FINANCIAL_ADVISOR_PARAGRAPHS}
                  emphasisFirst
                />
              </LegalLedgerRow>
              <LegalLedgerRow
                number="5.2"
                title="5.2 AI-Generated Content Disclaimer"
              >
                <p>{AI_OUTPUTS_INTRO}</p>
                <LegalBulletList items={AI_DISCLAIMER_ITEMS} />
              </LegalLedgerRow>
              <LegalLedgerRow
                number="5.3"
                title="5.3 Professional Responsibility and Duty of Care"
              >
                <p>{PROFESSIONAL_RESPONSIBILITY_INTRO}</p>
                <LegalBulletList items={PROFESSIONAL_RESPONSIBILITY} />
              </LegalLedgerRow>
            </LegalLedger>
          ),
        },
        {
          id: "limitation-of-liability",
          eyebrow: "Limitation of liability",
          title: "6. Limitation of Liability",
          children: (
            <LegalParagraphs
              paragraphs={LIMITATION_OF_LIABILITY_PARAGRAPHS}
              emphasisFirst
            />
          ),
        },
      ]}
      cta={
        <LegalCtaBand
          eyebrow="Questions about these Terms?"
          title="Questions about these Terms?"
          description="If you have any questions about these Terms of Service, please reach out to our team."
          primary="Contact Us"
          primaryHref={`mailto:${SUPPORT_EMAIL}`}
          secondary="Privacy Policy"
          secondaryHref="/privacy"
        />
      }
    />
  );
}
